<template>
  <div>
    <div class="adm-toolbar">
      <div class="adm-tabs" role="tablist" aria-label="Filter by status">
        <button v-for="tab in tabs" :key="tab.value" type="button" class="adm-tab" role="tab"
                :class="{ 'is-active': status === tab.value }" :aria-selected="status === tab.value"
                @click="setStatus(tab.value)">
          {{ tab.label }}
          <span v-if="tab.count !== null" class="adm-tab__count">{{ tab.count }}</span>
        </button>
      </div>
      <div class="adm-toolbar__spacer"></div>
      <div class="adm-search">
        <i class="fas fa-search"></i>
        <input v-model="search" class="adm-input" placeholder="Search name, email or specialty" aria-label="Search doctors" />
      </div>
    </div>

    <div class="adm-card">
      <div class="adm-table-wrap">
        <table class="adm-table">
          <thead>
            <tr>
              <th>Doctor</th>
              <th>Contact</th>
              <th>City</th>
              <th>Status</th>
              <th class="adm-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr v-for="n in 5" :key="`s${n}`">
                <td><div class="adm-person"><span class="adm-avatar"></span><div class="adm-skeleton" style="width: 160px"></div></div></td>
                <td><div class="adm-skeleton" style="width: 180px"></div></td>
                <td><div class="adm-skeleton" style="width: 80px"></div></td>
                <td><div class="adm-skeleton" style="width: 70px"></div></td>
                <td></td>
              </tr>
            </template>
            <tr v-else-if="!doctors.length">
              <td colspan="5">
                <div class="adm-empty">
                  <i class="fas fa-user-md"></i>
                  <strong>No doctors found</strong>
                  {{ search ? "Try a different search." : status === "pending" ? "No registrations are waiting for approval." : "Nothing to show for this filter." }}
                </div>
              </td>
            </tr>
            <template v-else>
            <tr v-for="doctor in doctors" :key="doctor.doctorid">
              <td>
                <button type="button" class="adm-person" @click="openDetails(doctor)">
                  <span class="adm-avatar">
                    <img v-if="images[doctor.doctorid]" :src="images[doctor.doctorid]" :alt="doctor.doctorname" />
                    <template v-else>{{ initials(doctor.doctorname) }}</template>
                  </span>
                  <span>
                    <span class="adm-person__name">{{ doctor.doctorname }}</span>
                    <span class="adm-person__meta">{{ doctor.specialization || "Specialization not set" }}</span>
                  </span>
                </button>
              </td>
              <td>
                <div>{{ doctor.email || "—" }}</div>
                <div class="adm-muted">{{ doctor.contactnumbers || "—" }}</div>
              </td>
              <td>{{ doctor.area || "—" }}</td>
              <td><span class="adm-badge" :class="`adm-badge--${doctor.status || 'pending'}`">{{ statusLabel(doctor.status) }}</span></td>
              <td class="adm-col-actions">
                <template v-if="isPending(doctor)">
                  <button type="button" class="adm-btn adm-btn--sm adm-btn--success" @click="approving = doctor">
                    <i class="fas fa-check"></i> Approve
                  </button>
                  <button type="button" class="adm-btn adm-btn--sm" @click="confirmStatus(doctor, 'rejected')">Reject</button>
                </template>
                <template v-else-if="doctor.status === 'active' || doctor.status === 'inactive'">
                  <button type="button" class="adm-btn adm-btn--sm" @click="$emit('navigate', 'slots', { doctorid: doctor.doctorid })">
                    <i class="fas fa-calendar-alt"></i> Slots
                  </button>
                  <button v-if="doctor.status === 'active'" type="button" class="adm-btn adm-btn--sm" @click="confirmStatus(doctor, 'inactive')">Deactivate</button>
                  <button v-else type="button" class="adm-btn adm-btn--sm" @click="setDoctorStatus(doctor, 'active')">Activate</button>
                </template>
                <button type="button" class="adm-icon-btn" title="View details" aria-label="View details" @click="openDetails(doctor)">
                  <i class="fas fa-chevron-right"></i>
                </button>
              </td>
            </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-if="pagination.total > 0" class="adm-pagination">
        <span>
          Showing {{ (pagination.page - 1) * pagination.per_page + 1 }}–{{ Math.min(pagination.page * pagination.per_page, pagination.total) }}
          of {{ pagination.total }}
        </span>
        <div class="adm-pagination__pages">
          <button type="button" class="adm-btn adm-btn--sm" :disabled="page <= 1" @click="goTo(page - 1)">
            <i class="fas fa-arrow-left"></i> Previous
          </button>
          <button type="button" class="adm-btn adm-btn--sm" :disabled="page >= pagination.total_pages" @click="goTo(page + 1)">
            Next <i class="fas fa-arrow-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Details -->
    <admin-modal v-if="viewing" size="lg" :title="viewing.doctorname" :subtitle="viewing.specialization || ''" @close="viewing = null">
      <div class="adm-doctor-head">
        <span class="adm-avatar adm-avatar--lg">
          <img v-if="images[viewing.doctorid]" :src="images[viewing.doctorid]" :alt="viewing.doctorname" />
          <template v-else>{{ initials(viewing.doctorname) }}</template>
        </span>
        <div>
          <span class="adm-badge" :class="`adm-badge--${viewing.status || 'pending'}`">{{ statusLabel(viewing.status) }}</span>
          <div class="adm-muted adm-doctor-head__id">Doctor ID {{ viewing.doctorid }}</div>
        </div>
      </div>
      <dl class="adm-dl">
        <dt>Email</dt><dd>{{ viewing.email || "—" }}</dd>
        <dt>Phone</dt><dd>{{ viewing.contactnumbers || "—" }}</dd>
        <dt>Qualifications</dt><dd>{{ viewing.qualifications || "—" }}</dd>
        <dt>Experience</dt><dd>{{ viewing.experience ? `${viewing.experience} years` : "—" }}</dd>
        <dt>Registration no.</dt><dd>{{ viewing.registration_info || "—" }}</dd>
        <dt>City</dt><dd>{{ viewing.area || "—" }}</dd>
        <dt>Languages</dt><dd>{{ viewing.languagesspoken || "—" }}</dd>
        <dt>Address</dt><dd>{{ viewing.address || "—" }}</dd>
      </dl>
      <h4 class="adm-section-title">Clinics</h4>
      <div v-if="viewingClinics === null" class="adm-skeleton" style="width: 50%"></div>
      <p v-else-if="!viewingClinics.length" class="adm-muted">Not assigned to any clinic yet.</p>
      <div v-else>
        <span v-for="clinic in viewingClinics" :key="clinic.clinicid" class="adm-chip">
          <i class="fas fa-hospital adm-muted"></i> {{ clinic.clinicname }}
        </span>
      </div>
      <template #footer>
        <template v-if="isPending(viewing)">
          <button type="button" class="adm-btn" @click="confirmStatus(viewing, 'rejected')">Reject</button>
          <button type="button" class="adm-btn adm-btn--success" @click="approving = viewing; viewing = null">
            <i class="fas fa-check"></i> Approve
          </button>
        </template>
        <button v-else type="button" class="adm-btn" @click="viewing = null">Close</button>
      </template>
    </admin-modal>

    <!-- Approve -->
    <approve-doctor-modal v-if="approving" :doctor="approving" :cities="cities"
                          @close="approving = null" @approved="onApproved" />

    <!-- Confirm reject / deactivate -->
    <admin-modal v-if="confirming" size="sm" :busy="saving"
                 :title="confirming.status === 'rejected' ? 'Reject registration?' : 'Deactivate doctor?'"
                 @close="confirming = null">
      <p class="adm-confirm-text">
        <template v-if="confirming.status === 'rejected'">
          <strong>{{ confirming.doctor.doctorname }}</strong> will not be able to sign in. This cannot be undone from here.
        </template>
        <template v-else>
          <strong>{{ confirming.doctor.doctorname }}</strong> will be hidden from patients and cannot take new bookings until you activate them again.
        </template>
      </p>
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="confirming = null">Cancel</button>
        <button type="button" class="adm-btn adm-btn--danger" :disabled="saving"
                @click="setDoctorStatus(confirming.doctor, confirming.status)">
          <span v-if="saving" class="adm-spinner"></span>
          {{ confirming.status === "rejected" ? "Reject" : "Deactivate" }}
        </button>
      </template>
    </admin-modal>
  </div>
</template>

<script>
import axios from "axios";
import AdminModal from "./AdminModal.vue";
import ApproveDoctorModal from "./ApproveDoctorModal.vue";
import { errorMessage, initials, STATUS_LABELS } from "../admin-utils";

const PER_PAGE = 10;

export default {
  name: "DoctorsPanel",
  // The shell passes every panel the same props; ignore the ones this panel does not use.
  inheritAttrs: false,
  components: { AdminModal, ApproveDoctorModal },
  props: {
    stats: { type: Object, default: () => ({}) },
    cities: { type: Array, default: () => [] },
    intent: { type: Object, default: null },
  },
  emits: ["notify", "changed", "navigate"],
  data() {
    return {
      status: "all",
      search: "",
      page: 1,
      doctors: [],
      pagination: { total: 0, page: 1, per_page: PER_PAGE, total_pages: 1 },
      loading: false,
      saving: false,
      images: {},
      viewing: null,
      viewingClinics: null,
      approving: null,
      confirming: null,
      requestId: 0,
    };
  },
  computed: {
    tabs() {
      return [
        { value: "all", label: "All", count: this.stats.total ?? null },
        { value: "pending", label: "Pending", count: this.stats.pending ?? null },
        { value: "active", label: "Active", count: this.stats.active ?? null },
        { value: "inactive", label: "Inactive", count: null },
        { value: "rejected", label: "Rejected", count: null },
      ];
    },
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.fetchDoctors();
      }, 300);
    },
  },
  methods: {
    initials,
    statusLabel(status) {
      return STATUS_LABELS[status] || "Pending";
    },
    isPending(doctor) {
      return !doctor.status || doctor.status === "pending";
    },
    setStatus(status) {
      this.status = status;
      this.page = 1;
      this.fetchDoctors();
    },
    goTo(page) {
      this.page = page;
      this.fetchDoctors();
    },
    async fetchDoctors() {
      const requestId = ++this.requestId;
      this.loading = true;
      try {
        const { data } = await axios.get("/doctors", {
          params: { page: this.page, per_page: PER_PAGE, status: this.status, searchtext: this.search.trim() || null },
        });
        if (requestId !== this.requestId) return;
        this.doctors = data.doctors || [];
        this.pagination = data.pagination;
        this.doctors.forEach(this.loadImage);
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.doctors = [];
        this.pagination = { total: 0, page: 1, per_page: PER_PAGE, total_pages: 1 };
        // The API answers 404 for an empty result.
        if (!error.response || error.response.status !== 404) {
          this.$emit("notify", errorMessage(error, "Could not load doctors."), "error");
        }
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async loadImage(doctor) {
      if (!doctor.doctorimage || this.images[doctor.doctorid] !== undefined) return;
      this.images[doctor.doctorid] = "";
      try {
        const { data } = await axios.get("doctors/doctor-images", {
          params: { profilePath: doctor.doctorimage },
          responseType: "arraybuffer",
        });
        this.images[doctor.doctorid] = URL.createObjectURL(new Blob([data]));
      } catch (error) {
        // Initials are shown instead.
      }
    },
    async openDetails(doctor) {
      this.viewing = doctor;
      this.viewingClinics = null;
      try {
        const { data } = await axios.get(`/admin/doctors/${doctor.doctorid}/clinics`);
        if (this.viewing === doctor) this.viewingClinics = data || [];
      } catch (error) {
        if (this.viewing === doctor) this.viewingClinics = [];
      }
    },
    confirmStatus(doctor, status) {
      this.viewing = null;
      this.confirming = { doctor, status };
    },
    async setDoctorStatus(doctor, status) {
      this.saving = true;
      try {
        await axios.patch(`/doctors/${doctor.doctorid}/status`, null, { params: { status } });
        const verb = { rejected: "rejected", inactive: "deactivated", active: "activated" }[status];
        this.$emit("notify", `${doctor.doctorname} was ${verb}.`);
        this.confirming = null;
        this.$emit("changed");
        await this.fetchDoctors();
      } catch (error) {
        this.$emit("notify", errorMessage(error, "Could not update the doctor."), "error");
      } finally {
        this.saving = false;
      }
    },
    async onApproved(result) {
      const doctor = this.approving;
      this.approving = null;
      this.$emit("notify", `${doctor.doctorname} is approved. Login details were emailed to ${result.email}.`);
      this.$emit("changed");
      await this.fetchDoctors();
    },
    applyIntent(intent) {
      if (!intent) return;
      if (intent.status) this.status = intent.status;
      if (intent.approve) this.approving = intent.approve;
    },
  },
  created() {
    this.applyIntent(this.intent);
    this.fetchDoctors();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
    Object.values(this.images).forEach(url => url && URL.revokeObjectURL(url));
  },
};
</script>

<style scoped>
.adm-doctor-head {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.adm-doctor-head__id {
  font-size: 13px;
  margin-top: 6px;
}

.adm-confirm-text {
  margin: 0;
}
</style>
